const CONSENT_KEY = 'sshop_tracking_consent';
const LEGACY_CONSENT_KEY = 'cookie_consent';
const VISITOR_KEY = 'sshop:activity-visitor';
const VISIT_KEY = 'sshop:activity-visit';
const LAST_ACTIVE_KEY = 'sshop:activity-last-active';
const VISIT_IDLE_MS = 30 * 60 * 1000;
const ATTRIBUTION_KEY = 'sshop:short-link-attribution';
let memoryAttribution: string | null = null;
let memoryConsent: 'granted' | 'denied' | null = null;
let memoryVisitor: string | null = null;
let memoryVisit: string | null = null;
let memoryLastActive = 0;

export function setActivityConsent(granted: boolean): void {
  memoryConsent = granted ? 'granted' : 'denied';
  if (!granted) clearActivityIdentity();
}

export function activityConsentChoice(): 'granted' | 'denied' | null {
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    if (stored === 'granted' || stored === 'denied') return stored;
    const legacy = window.localStorage.getItem(LEGACY_CONSENT_KEY);
    if (legacy === 'granted' || legacy === 'denied') return legacy;
  } catch { /* private mode may block storage */ }
  return memoryConsent;
}

export function activityUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto.getRandomValues !== 'function') throw new Error('Secure random source unavailable');
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
}

export function activityHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    if (activityConsentChoice() !== 'granted') return {};
    let visitor: string | null = null;
    let visit: string | null = null;
    let last = memoryLastActive;
    try {
      visitor = window.localStorage.getItem(VISITOR_KEY);
      visit = window.sessionStorage.getItem(VISIT_KEY);
      last = Number(window.sessionStorage.getItem(LAST_ACTIVE_KEY) ?? 0);
    } catch { /* use this tab's in-memory identity */ }
    visitor = visitor || memoryVisitor;
    if (!visitor) {
      visitor = activityUuid();
      try { window.localStorage.setItem(VISITOR_KEY, visitor); } catch { /* memory fallback */ }
    }
    memoryVisitor = visitor;
    visit = visit || memoryVisit;
    if (!visit || Date.now() - last > VISIT_IDLE_MS) {
      visit = activityUuid();
      try { window.sessionStorage.setItem(VISIT_KEY, visit); } catch { /* memory fallback */ }
    }
    memoryVisit = visit;
    memoryLastActive = Date.now();
    try { window.sessionStorage.setItem(LAST_ACTIVE_KEY, String(memoryLastActive)); } catch { /* memory fallback */ }
    // Public signed reference carries no visitor identity. Persist only after consent.
    const incoming = new URLSearchParams(window.location.search).get('sl_ref');
    if (incoming && /^[a-z0-9_-]{3,64}\.[0-9]{10}\.[a-f0-9]{16}\.[a-f0-9]{64}$/.test(incoming)) {
      memoryAttribution = incoming;
      try { window.sessionStorage.setItem(ATTRIBUTION_KEY, incoming); } catch { /* memory fallback */ }
    }
    let attribution = memoryAttribution;
    try { attribution = window.sessionStorage.getItem(ATTRIBUTION_KEY) || attribution; } catch { /* memory fallback */ }
    return {
      'X-Activity-Consent': 'granted',
      'X-Activity-Visitor': visitor,
      'X-Activity-Visit': visit,
      'X-Activity-Page': window.location.pathname,
      ...(attribution ? { 'X-Short-Link-Attribution': attribution } : {}),
    };
  } catch {
    return {};
  }
}

export function clearActivityIdentity(): void {
  memoryVisitor = null;
  memoryVisit = null;
  memoryLastActive = 0;
  memoryAttribution = null;
  try {
    window.localStorage.removeItem(VISITOR_KEY);
    window.sessionStorage.removeItem(VISIT_KEY);
    window.sessionStorage.removeItem(LAST_ACTIVE_KEY);
    window.sessionStorage.removeItem(ATTRIBUTION_KEY);
  } catch {
    /* storage may be disabled */
  }
}
